import { Eye } from 'lucide-react'
import { CSSProperties, useEffect, useRef, useState, ReactNode } from 'react'
import { useCreativePreview } from './model'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    LoadingIndicator,
} from '@/shared/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { EyeAltSVGIcon } from '@/shared/assets'
import { cn } from '@/true-fsd/shared/utils/css'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'

export type CreativePreviewProps = WorkspaceWithLinkDescriptor & {
    published: boolean
    privateChannel: boolean
    postDeleted: boolean
    overlayStyle?: CSSProperties
    contentStyle?: CSSProperties
}

export const CreativePreview = ({
    published,
    privateChannel,
    postDeleted,
    overlayStyle,
    contentStyle,
    ...props
}: CreativePreviewProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    const { data, isLoading, isError, error } = useCreativePreview(props, {
        enabled: open,
    })
    const [previewKey, setPreviewKey] = useState(0)

    useEffect(() => {
        if (open) {
            setPreviewKey((prev) => prev + 1)
        }
    }, [open])

    useEffect(() => {
        if (!data || !data?.url.length || !open) return
        const container = containerRef.current
        if (!container) return

        container.innerHTML = ''

        const script = document.createElement('script')
        script.src = 'https://telegram.org/js/telegram-widget.js?22'
        script.async = true
        script.setAttribute(
            'data-telegram-post',
            data.url.replace('https://t.me/', '')
        )
        script.setAttribute('data-width', '100%')

        script.onload = () => {
            setTimeout(() => {
                const iframe = container.querySelector('iframe')
                if (iframe) {
                    if (data.isDeleted) {
                        iframe.style.pointerEvents = 'none'
                    }
                }
            }, 1000)
        }

        container.appendChild(script)

        return () => {
            container.innerHTML = ''
        }
    }, [data, open, previewKey])

    let template: ReactNode | null = null

    if (isLoading) {
        template = (
            <LoadingIndicator
                style={{
                    width: '20px',
                    height: '20px',
                    margin: '0 auto',
                }}
            />
        )
    }

    if (isError) {
        template = (
            <div className="text-center text-gray-500">
                Ошибка при загрузке предпросмотра: {error.message}
            </div>
        )
    }

    if (data && open) {
        template = (
            <>
                <div
                    ref={containerRef}
                    className={cn(
                        'relative overflow-hidden',
                        data.isDeleted && ''
                    )}
                    style={{
                        height: data.isDeleted ? 'calc(100% - 30px)' : '100%',
                    }}
                />
                {data.isDeleted && (
                    <div className="flex items-center justify-end gap-[6px]">
                        <EyeAltSVGIcon />
                        <span className="text-[13px] font-light text-secondary">
                            {data.view}
                        </span>
                    </div>
                )}
            </>
        )
    }

    if (data && !data.url.length && open) {
        template = (
            <div className="text-center text-gray-500">
                Нет поста для предпросмотра
            </div>
        )
    }

    const nonDeletedPrivate = !postDeleted && privateChannel
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Tooltip>
                <DialogTrigger asChild>
                    {!published || nonDeletedPrivate ? (
                        <TooltipTrigger asChild>
                            <div
                                className="box-border flex h-[30px] min-w-[26px] items-center justify-center rounded-[4px] border border-secondary opacity-50"
                                onClick={(e) => e.preventDefault()}
                            >
                                <Eye color="#94A6AF" width={14} height={14} />
                            </div>
                        </TooltipTrigger>
                    ) : (
                        <button
                            className="box-border flex h-[30px] min-w-[26px] items-center justify-center rounded-[4px] border border-[#056FFF]"
                            onClick={() => setOpen(true)}
                        >
                            <Eye color="#056FFF" width={14} height={14} />
                        </button>
                    )}
                </DialogTrigger>
                <TooltipContent side="top">
                    {nonDeletedPrivate ? (
                        <p>
                            Для закрытых каналов просмотр опубликованного поста
                            недоступен
                        </p>
                    ) : (
                        <p>Креатив ещё не опубликован</p>
                    )}
                </TooltipContent>
            </Tooltip>

            <DialogContent
                className="max-h-[80vh] overflow-y-auto bg-white"
                overlayStyle={overlayStyle}
                style={contentStyle}
            >
                <DialogHeader>
                    {data?.isDeleted && (
                        <DialogTitle className="text-center">
                            Просмотр удаленного поста
                        </DialogTitle>
                    )}
                </DialogHeader>
                {template}
            </DialogContent>
        </Dialog>
    )
}
